'use client'

import { cn } from '@brew/ui/cn'
import { Building2 } from 'lucide-react'
import { useState } from 'react'
import type { IntegrationProvider } from '@/lib/data/convex/integrations'
import { integrationLogoAssetUrl } from '@/lib/integrations/integration-logo-assets'

export type IntegrationBrandLogoProps = {
  /**
   * @deprecated retained for back-compat with existing call sites; the
   * logo URL now resolves purely from `provider` against the Brew CDN
   * map. The prop is accepted (and ignored) so consumers don't need to
   * change in lockstep.
   */
  brandDomain?: string
  provider: IntegrationProvider
  name: string
  /** Grid card vs dialog header — drives icon vs wordmark variant. */
  variant: 'card' | 'dialog'
  /** Optional CSS-filter tint to recolor the rasterized brand mark. */
  tone?: 'black'
  /**
   * Optional background tile painted behind the logo. `'black'` is for
   * brands whose mark is white-on-dark (e.g. Iterable) so they stay legible
   * on the light-mode card surface.
   */
  background?: 'black'
  /** Outer wrapper (flex centering, size). */
  className?: string
  /** Applied to the img (sizing inside the wrapper). */
  imgClassName?: string
}

/**
 * Renders an integration's brand mark from the Brew CDN
 * (`https://cdn.brew.new/integrations/integration-<provider>-<icon|wordmark>.png`).
 *
 * Single network request, no runtime logo-lookup / Google-favicon
 * fallback chain. On image load failure (e.g. the PNG temporarily
 * 404s) the component degrades to a neutral `Building2` glyph
 * matching the "no logo available" tile style used elsewhere in the
 * admin UI. The Mongo-backed `INTEGRATION_LOGO_ASSETS` map is the
 * single source of truth across every consumer.
 */
export function IntegrationBrandLogo({
  provider,
  name,
  variant,
  tone,
  background,
  className,
  imgClassName,
}: IntegrationBrandLogoProps) {
  const src = integrationLogoAssetUrl(provider, variant)
  const [hasLoadFailed, setHasLoadFailed] = useState(false)

  const wrapperClass = cn(
    'flex items-center justify-center',
    {
      'bg-black p-1': background === 'black',
    },
    className
  )

  if (hasLoadFailed) {
    return (
      <span
        className={cn('text-muted-foreground', wrapperClass)}
        role="img"
        aria-label={`${name} logo`}
      >
        <Building2 className={cn('size-6', imgClassName)} aria-hidden />
      </span>
    )
  }

  return (
    <span className={wrapperClass}>
      {/* biome-ignore lint/performance/noImgElement: Brew CDN URLs are not Next-optimised */}
      {/** biome-ignore lint/correctness/useImageSize: pre-Ultracite baseline — add dimensions when touching */}
      <img
        src={src}
        alt=""
        // Preserve real brand colors in both themes. Per-brand overrides
        // (`tone: 'black'` / `background: 'black'`) flow through.
        className={cn(
          'object-contain',
          { 'brightness-0': tone === 'black' },
          imgClassName,
          { '!object-contain': background === 'black' }
        )}
        loading="lazy"
        decoding="async"
        sizes={variant === 'card' ? '44px' : '112px'}
        onError={() => setHasLoadFailed(true)}
      />
    </span>
  )
}
